// LoginPage.js
import React, { useEffect } from "react";
import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google";
import moment from "moment";
import axios from "axios";
import api from "../api";
import "./Login.css";
import { useAmazonContext } from "../../Contexts/AmazonContext";
import { useLocation, useHistory } from "react-router-dom";
import { usePostHog } from 'posthog-js/react';

const LoginPage = ({ setIsLoggedIn, setTokenExpiration }) => {
  const { userRole, setUserRole, setShowUpdateModal, setUpdateInfo, fetchUserStreak, fetchUserLessons } =
    useAmazonContext();
  const location = useLocation();
  const history = useHistory();
  const posthog = usePostHog();

  const convertToCloudFrontUrl = (s3Url) => {
    if (!s3Url) return s3Url;
    return s3Url.replace(
      "https://hhcourses-assets.s3.us-east-2.amazonaws.com",
      "https://d10jmd9bhq0s4v.cloudfront.net"
    );
  };

  // Function to handle login
  const handleLogin = () => {
    // Perform login logic
    // Set isLoggedIn to true and set token expiration time
    setIsLoggedIn(true);

    // Set expiration time to 30 minutes from now
    // const expirationTime = new Date();
    // expirationTime.setTime(expirationTime.getTime() + (180 * 60 * 1000)); // 30 minutes in milliseconds
    // setTokenExpiration(expirationTime);

    // Set expiration time to infinity
    const expirationTime = new Date("9999-12-31T23:59:59"); // Any date far in the future
    setTokenExpiration(expirationTime);

    // Store isLoggedIn and expirationTime in localStorage
    localStorage.setItem("isLoggedIn", true);
    localStorage.setItem("tokenExpiration", expirationTime.getTime()); // Store as timestamp

    // Check if there's a redirect URL in the query params
    const searchParams = new URLSearchParams(location.search);
    const redirectUrl = searchParams.get("redirect");

    if (redirectUrl) {
      history.push(redirectUrl);
    } else {
      history.push("/home"); // Changed from "/" to "/home" for default redirect
    }
  };

  const CustomButton = () => {
    const Login = useGoogleLogin({
      onSuccess: async (tokenResponse) => {
        console.log(tokenResponse);
        let userObject;
        try {
          // fetching userinfo can be done on the client or the server
          userObject = await api
            .get("https://www.googleapis.com/oauth2/v3/userinfo", {
              headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
            })
            .then((res) => res.data);

          const response = await api.post("/updateUser", {
            userEmail: userObject.email,
            givenName: userObject.given_name,
            familyName: userObject.family_name,
            isLogin: true,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Add user's timezone
          });

          const user = response?.data;
          localStorage.setItem("userEmail", userObject.email);
          localStorage.setItem(
            "userGivenName",
            user?.displayGivenName || user?.givenName || userObject.given_name
          );
          localStorage.setItem(
            "userFamilyName",
            user?.displayFamilyName || user?.familyName || userObject.family_name
          );

          posthog?.identify(userObject.email, {
            email: userObject.email,
            name: `${userObject.given_name} ${userObject.family_name}`,
            given_name: userObject.given_name,
            family_name: userObject.family_name,
          });

          handleLogin();
        } catch (error) {
          console.error("Error updating user:", error);
          history.replace("/something-went-wrong");
          return;
        }

        console.log(userObject);

        // Get the current date and time in the specified format
        const formattedDate = moment().format("DD-MM-YYYY hh:mm:ss");
        console.log(formattedDate);

        // Combine user data and formatted date into a single object
        const requestData = {
          userEmail: userObject.email,
          userFamilyName: userObject.family_name,
          userGivenName: userObject.given_name,
          userPicture: userObject.picture,
          loginDateTime: formattedDate,
        };
        console.log(requestData);

        api
          .post("/insertLoginRecord", requestData)
          .then((response) => {
            if (response.status === 200) {
              return response.data; // If you expect a response from the server
            }
            throw new Error("Network response was not ok.");
          })
          .then((data) => {
            console.log(data); // Log response from the server
          })
          .catch((error) => {
            if (
              error instanceof SyntaxError &&
              error.message.includes("Unexpected token 'U'")
            ) {
              // Ignore the SyntaxError caused by unexpected token 'U'
              return;
            }
            console.error("Error from fetch:", error); // Log other errors
          });

        const userEmail = localStorage.getItem("userEmail");
        if (!userEmail) {
          console.error("User email not found in local storage");
          return;
        }

        const fetchUserRole = async () => {
          try {
            const response = await api.get("/fetchUserRole", {
              params: {
                userEmail: userEmail,
              },
            });
            setUserRole(response.data.role);
            localStorage.setItem("userRole", response.data.role);
            console.log(localStorage.getItem("userRole"));

            // Add update modal check after fetchUserRole
            try {
              const updateResponse = await api.get("/checkUpdateStatus", {
                params: { userEmail: userObject.email },
              });

              if (updateResponse.data.showUpdate) {
                setShowUpdateModal(true);
                setUpdateInfo(updateResponse.data);
              }
            } catch (error) {
              console.error("Error checking for updates:", error);
            }
          } catch (error) {
            console.error("Error fetching user role:", error);
            setUserRole("user");
            localStorage.setItem("userRole", "user");
            console.log(localStorage.getItem("userRole"));
          }
        };

        fetchUserRole();

        // Fetch user streak and lesson counts immediately after login
        fetchUserStreak();
        fetchUserLessons();
      },
      // flow: 'implicit', // implicit is the default
    });

    return (
      <div
        onClick={() => {
          Login();
        }}
        className="GetStartedButton"
        style={{ background: "white", color: "black" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={convertToCloudFrontUrl(
              "https://hhcourses-assets.s3.us-east-2.amazonaws.com/General/CanvaGeneratedImages/GoogleLogo.png"
            )}
            alt="Google logo"
            style={{ width: "10%", height: "auto", marginRight: "25px" }}
          />
          <div>Continue with Google</div>
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        flexDirection: "column",
      }}
    >
      <div>
        <div className="LoginMessageHeader">Time to Log In!</div>
        <div className="LoginMessage">
          {" "}
          Save your progress,
          <br /> sync across devices
          <br /> and more
        </div>
      </div>
      <GoogleOAuthProvider clientId="937262274332-2l7pmq1m0d5r8fmrlluqhka6ate7ea6r.apps.googleusercontent.com">
        <CustomButton />
      </GoogleOAuthProvider>
    </div>
  );
};

export default LoginPage;
